import { useRouter } from 'next/router';
import { useCallback } from 'react';
import sortBy from 'lodash/sortBy';
import { getQueryStringParams, addQueryStringParam } from '@framework/url/helpers/queryString';
import { IProduct } from '@/creditflowresearch/domain/vo/Product';
import { IRegion } from '@/creditflowresearch/domain/vo/Region';
import { getValidChangedProductsRegions } from '@/creditflowresearch/application/helpers';
import { selectProductAndRegionIdsFromQuery } from '@/creditflowresearch/application/PageFilters/selectProductAndRegionIdsFromQuery';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';

interface IUseProductRegionFiltersProps {
  productsCollection: IProduct[],
  regionsCollection: IRegion[],
  onFiltersChange: (productIds: string[], regionIds: string[]) => void,
}

export const useProductRegionFilters = ({
  productsCollection,
  regionsCollection,
  onFiltersChange,
}: IUseProductRegionFiltersProps) => {
  const router = useRouter();
  const { plan, products, regions } = useUserPlanContext();

  const getCurrentState = () => {
    const location = {
      search: router.asPath.includes('?') ? `?${ router.asPath.split('?')[1] }` : '',
    };
    const query = getQueryStringParams(location);

    return selectProductAndRegionIdsFromQuery({
      pathname: router.asPath,
      query,
      regionsList: regionsCollection,
      productsList: productsCollection,
      plan,
      accessibleProductsIds: products,
      accessibleRegionsIds: regions,
    });
  };

  const { productIds: activeProductIds, regionIds: activeRegionIds } = getCurrentState();

  const initProductRegionFilters = useCallback((
    shouldSetFilterFromHeader: boolean = false,
  ) => {
    const location = {
      search: router.asPath.includes('?') ? `?${ router.asPath.split('?')[1] }` : '',
    };
    const query = getQueryStringParams(location);

    let modifiedQuery = { ...query };
    if (shouldSetFilterFromHeader) {
      const allProductsAbbreviations = sortBy(productsCollection.map((product) => product.abbreviation));
      const allRegionsAbbreviations = sortBy(regionsCollection.map((region) => region.abbreviation));

      if (!query.products) {
        modifiedQuery.products = allProductsAbbreviations.join(',');
      }
      if (!query.regions) {
        modifiedQuery.regions = allRegionsAbbreviations.join(',');
      }
    }

    const {
      validProducts,
      productsChanged,
      validRegions,
      regionsChanged,
    } = getValidChangedProductsRegions({
      products: modifiedQuery.products,
      productsList: productsCollection,
      regions: modifiedQuery.regions,
      regionsList: regionsCollection,
      pathname: router.pathname,
    });

    let hasChanges = false;

    if (productsChanged || regionsChanged) {
      const queryParam = addQueryStringParam(location, {
        products: validProducts,
        regions: validRegions,
      });

      const currentPath = router.asPath.split('?')[0];
      router.replace(`${ currentPath }?${ queryParam }`, undefined, { shallow: true });
      hasChanges = true;
    }

    if (hasChanges) {
      const newState = getCurrentState();
      onFiltersChange(newState.productIds, newState.regionIds);
      return true;
    }

    return false;
  }, [router, productsCollection, regionsCollection, onFiltersChange, activeProductIds, activeRegionIds]);

  return {
    initProductRegionFilters,
    activeProductIds,
    activeRegionIds,
  };
};
